import React, { Component } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { parse as qsParse, stringify as qsStringify } from 'query-string';
import { debounce, isEmpty as empty } from 'lodash';
import {
  TableSortIcon,
  ButtonPrimary,
  colorThemeGenerator,
} from '@spotify-internal/encore-creator-web';
import { scroller as scroll } from 'react-scroll';

import { ConnectedFilterWithDatePicker } from '../../Shared/FilterWithDatePicker';
import { Pagination } from '../../Shared/Pagination';
import { LoadingIndicator } from '../../Shared/LoadingIndicator';

import { PodcastPending } from '../PodcastPending';
import styles from './index.module.scss';
import {
  LOADING,
  ERROR,
  selectDateRange,
  selectUserShows,
  selectUserShowsStatus,
  selectPendingPodcasts,
  selectPendingPodcastsStatus,
  selectNetworkMetadata,
  selectCurrentNetworkId,
  selectFeatures,
} from '../../../store/selectors';
import { loadUserShows, loadPendingPodcasts } from '../../../store/meShows';
import { downloadPodcastListAsCsv } from '../../../store/downloadCsv';
import { trackEvent } from '../../../lib/Tracking/index';
import { Helmet } from 'react-helmet';
import { MeContext } from '../../../context/me';
import { screenMdMin } from '@spotify-internal/encore-foundation';
import { MeShows, PaginatedShows, ReduxState, FeatureMap } from '../../../types';
import { TableHeaderLabel, TableHeaderCell, TableRowLink } from '../../Shared/Table';

import { Banner, Table, TableRow, TableCell, TableThumbnail } from '@spotify-internal/encore-web';

import styled, { ThemeProvider } from 'styled-components';
import {
  FormattedMessage,
  injectIntl,
  IntlShape,
  defineMessage,
  FormattedDateParts,
} from 'react-intl';
import { isS4PodShowWrappedBannersEnabled } from '../../../lib/Features';
import { logger } from '../../../lib/Api';
import { getWrappedButtonTheme } from '../utils/wrapped';

type Props = {
  networkPodcastList?: {
    shows: MeShows[];
  } & PaginatedShows;
  status?: typeof LOADING | typeof ERROR;
  pendingPodcastsStatus?: typeof LOADING | typeof ERROR;
  loadUserShows: typeof loadUserShows;
  downloadPodcastListAsCsv: typeof downloadPodcastListAsCsv;
  dateRange?: {
    startDate: string;
    endDate: string;
    label: string;
  };
  pendingPodcasts?: {
    artworkUrl?: string;
    name?: string;
    author?: string;
    status?: string;
  }[];
  loadPendingPodcasts: typeof loadPendingPodcasts;
  networkMetadata?: {
    name?: string;
  };
  currentNetworkId?: string;
  intl: IntlShape;
  features: FeatureMap;
} & RouteComponentProps;

type State = {
  sortBy: string;
  sortDescending: boolean;
  isMobile: boolean;
};

const MediaQueryList = window.matchMedia(`(min-width: ${screenMdMin})`);

export class InternationalizedPodcastList extends Component<Props, State> {
  state = {
    sortBy: 'name',
    sortDescending: false,
    isMobile: !MediaQueryList.matches,
    isWrappedEnabled: isS4PodShowWrappedBannersEnabled(this.props.features),
  };

  static contextType = MeContext;

  matchMediaListener =
    /* istanbul ignore next */
    event => {
      /* istanbul ignore next */
      this.setState({ isMobile: !event.matches });
    };

  isTryingToLoadEntireCatalog = () => {
    // If there isn't a network ID set AND the user has god mode, then S4P will try and load the entire Spotify catalog and time out
    return !this.props.currentNetworkId && this.context.permissions.godMode;
  };

  componentDidMount() {
    if (!this.isTryingToLoadEntireCatalog()) {
      this.props.loadUserShows(
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
      this.props.loadPendingPodcasts();
    }

    MediaQueryList.addListener(this.matchMediaListener);
  }

  componentWillUnmount() {
    MediaQueryList.removeListener(this.matchMediaListener);
  }

  /* istanbul ignore next */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.dateRange) return;
    if (
      nextProps.dateRange.startDate !== this.props.dateRange.startDate ||
      nextProps.dateRange.endDate !== this.props.dateRange.endDate
    ) {
      this.props.loadUserShows(
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
    }
  }

  /* istanbul ignore next */
  async componentDidUpdate() {
    if (
      this.props.networkPodcastList &&
      this.props.networkPodcastList.currentPage !== this.currentPage
    ) {
      await this.props.loadUserShows(
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
      scroll.scrollTo('filter-with-date-picker');
    }
  }

  get queryParams() {
    return qsParse(this.props.location.search);
  }

  get searchQuery() {
    if (typeof this.queryParams.search === 'string') {
      return this.queryParams.search;
    }

    return '';
  }

  get currentPage() {
    if (typeof this.queryParams.page === 'string') {
      return parseInt(this.queryParams.page, 10);
    }

    return 1;
  }

  filterPodcastList = debounce(
    /* istanbul ignore next */
    () => {
      this.props.loadUserShows(
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
    },
    500,
  );

  formatQueryParams = params => qsStringify({ ...this.queryParams, ...params });

  handleTableRowClick = podcastId => () => this.props.history.push(`/podcast/${podcastId}`);

  formatValueByDataAvailability = (key, podcast) => {
    if (podcast[key] === null) return '-';
    return this.props.intl.formatNumber(podcast[key]);
  };

  handlePageChange() {
    return ({ selected }) => {
      const { history, location } = this.props;
      const queryParams = this.formatQueryParams({ page: selected });

      history.push(`${location.pathname}?${queryParams}${location.hash || ''}`);
      trackEvent({ category: 'podcast-list', action: 'page-change', label: selected });
      scroll.scrollTo('filter-with-date-picker');

      return this.props.loadUserShows(
        this.searchQuery,
        selected,
        this.state.sortBy,
        this.state.sortDescending,
      );
    };
  }

  handleSearchInput() {
    return query => {
      trackEvent({ category: 'podcast', action: 'search', label: query });

      const { history, location } = this.props;
      const isEmptyQuery = empty(query.trim());
      /* istanbul ignore next */
      const queryParams = this.formatQueryParams({
        search: isEmptyQuery ? undefined : query,
        page: 1,
      });
      history.replace(`${location.pathname}?${queryParams}${location.hash || ''}`);
      this.filterPodcastList();
    };
  }

  handleColumnSort = columnKey => () => {
    let sortDescending = true;
    /* istanbul ignore else */
    if (columnKey === this.state.sortBy) {
      sortDescending = !this.state.sortDescending;
    }
    trackEvent({ category: 'podcast-list', action: 'sort', label: columnKey });
    this.setState({ sortBy: columnKey, sortDescending }, () => {
      this.props.loadUserShows(
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
    });
  };

  handleCsvDownload = /* istanbul ignore next */ () => {
    this.props.downloadPodcastListAsCsv(
      this.searchQuery,
      this.state.sortBy,
      this.state.sortDescending,
    );
  };

  handleWrappedLinkClick = podcastId => {
    return async event => {
      event.stopPropagation();
      await logger.wrappedCatalogClick(podcastId);
      this.handleTableRowClick(podcastId)();
    };
  };

  renderColumnSortIcon = (columnKey: string) => {
    if (columnKey === this.state.sortBy) {
      return <TableSortIcon direction={this.state.sortDescending ? 'down' : 'up'} />;
    }
    return null;
  };

  renderTableHead = () => {
    const { intl, networkPodcastList } = this.props;
    const { isWrappedEnabled, isMobile, sortBy } = this.state;
    const isDesktop = !isMobile;

    const podcastHeaderMsg = defineMessage({
      id: 'podcast-list.table-header.podcasts',
      defaultMessage: `{count, plural,
        one {# podcast}
        other {# podcasts}
      }`,
    });
    const startsHeaderMsg = defineMessage({
      id: 'podcast-list.table-header.starts',
      defaultMessage: 'Starts',
    });
    const streamsHeaderMsg = defineMessage({
      id: 'podcast-list.table-header.streams',
      defaultMessage: 'Streams',
    });
    const listenersHeaderMsg = defineMessage({
      id: 'podcast-list.table-header.listeners',
      defaultMessage: 'Listeners',
    });
    const releaseDateHeaderMsg = defineMessage({
      id: 'podcast-list.table-header.release-date',
      defaultMessage: 'Last Episode Date',
    });
    const podcastHeader = intl.formatMessage(podcastHeaderMsg, {
      count: networkPodcastList!.totalShows,
    });
    const startsHeader = intl.formatMessage(startsHeaderMsg);
    const streamsHeader = intl.formatMessage(streamsHeaderMsg);
    const listenersHeader = intl.formatMessage(listenersHeaderMsg);
    const releaseDateHeader = intl.formatMessage(releaseDateHeaderMsg);

    return (
      <TableRow>
        <TableHeaderCell onClick={this.handleColumnSort('name')}>
          <TableHeaderLabel
            style={{ justifyContent: 'flex-start' }}
            active={sortBy === 'name'}
            label={podcastHeader}
            childrenOrderedFirst={false}
          >
            {this.renderColumnSortIcon('name')}
          </TableHeaderLabel>
        </TableHeaderCell>
        <TableHeaderCell onClick={this.handleColumnSort('starts')}>
          <TableHeaderLabel active={sortBy === 'starts'} label={startsHeader}>
            {this.renderColumnSortIcon('starts')}
          </TableHeaderLabel>
        </TableHeaderCell>
        {isDesktop && (
          <>
            <TableHeaderCell onClick={this.handleColumnSort('streams')}>
              <TableHeaderLabel active={sortBy === 'streams'} label={streamsHeader}>
                {this.renderColumnSortIcon('streams')}
              </TableHeaderLabel>
            </TableHeaderCell>
            <TableHeaderCell onClick={this.handleColumnSort('listeners')}>
              <TableHeaderLabel active={sortBy === 'listeners'} label={listenersHeader}>
                {this.renderColumnSortIcon('listeners')}
              </TableHeaderLabel>
            </TableHeaderCell>
            <TableHeaderCell onClick={this.handleColumnSort('releaseDate')}>
              <TableHeaderLabel active={sortBy === 'releaseDate'} label={releaseDateHeader}>
                {this.renderColumnSortIcon('releaseDate')}
              </TableHeaderLabel>
            </TableHeaderCell>
          </>
        )}
        {isWrappedEnabled && <TableHeaderCell className={classNames(styles.bordered)} />}
      </TableRow>
    );
  };

  renderTableBody(podcasts) {
    const { isWrappedEnabled, isMobile } = this.state;
    const isDesktop = !isMobile;

    return podcasts.map(podcast => {
      const [year, month, day] = podcast.releaseDate
        ? podcast.releaseDate.split('-')
        : [null, null, null];

      return (
        <TableRow
          key={podcast.id}
          hover
          onClick={this.handleTableRowClick(podcast.id)}
          data-testid="podcast-list-table-row"
          style={{ cursor: 'pointer' }}
        >
          <TableCell className={classNames(styles['artwork-cell'], styles.bordered)}>
            <TableThumbnail
              img={podcast.artworkUrl}
              imgAlt="Podcast Artwork"
              thumbnailTitle={
                <TableRowLink to={`/podcast/${podcast.id}`}>{podcast.name}</TableRowLink>
              }
              small
            />
          </TableCell>
          <TableCell align="right" numerical>
            {this.formatValueByDataAvailability('starts', podcast)}
          </TableCell>
          {isDesktop && (
            <>
              <TableCell align="right" numerical>
                {this.formatValueByDataAvailability('streams', podcast)}
              </TableCell>
              <TableCell align="right" numerical>
                {this.formatValueByDataAvailability('listeners', podcast)}
              </TableCell>
              <TableCell align="right" numerical>
                <FormattedDateParts
                  value={new Date(year, month - 1, day)}
                  year="numeric"
                  month="short"
                  day="numeric"
                >
                  {parts => (
                    <>
                      {parts[2].value.toUpperCase()}
                      {parts[1].value.toUpperCase()}
                      {parts[0].value.toUpperCase()}
                      {parts[1].value.toUpperCase()}
                      {parts[4].value.toUpperCase()}
                    </>
                  )}
                </FormattedDateParts>
              </TableCell>
            </>
          )}
          {isWrappedEnabled && this.renderWrappedTableCell(podcast.id)}
        </TableRow>
      );
    });
  }

  renderColGroup = () => {
    const { isWrappedEnabled, isMobile } = this.state;
    const isDesktop = !isMobile;
    const wrappedCol = isWrappedEnabled && <col width="100" />;

    /* istanbul ignore else */
    if (isDesktop) {
      return (
        <colgroup>
          <col />
          <col width="100" />
          <col width="100" />
          <col width="100" />
          <col width="160" />
          {wrappedCol}
        </colgroup>
      );
    }

    /* istanbul ignore next */
    return (
      <colgroup>
        <col />
        <col width="100" />
        {wrappedCol}
      </colgroup>
    );
  };

  renderLoader = () => (
    <div className={styles.loader}>
      <LoadingIndicator style={{ height: '35vh', overflow: 'hidden' }} />
    </div>
  );

  renderWrappedTableCell = podcastId => {
    return (
      <TableCell align="right">
        <WrappedLink onClick={this.handleWrappedLinkClick(podcastId)}>
          <FormattedMessage id="podcast-list.wrapped-button" defaultMessage="Wrapped" />
        </WrappedLink>
      </TableCell>
    );
  };

  render() {
    const podcastList = this.props.networkPodcastList;

    if (this.props.status === ERROR) {
      return <span data-qa="network-podcast-list-error" />;
    }

    if (this.isTryingToLoadEntireCatalog()) {
      return (
        <Banner data-testid="god-mode-catalog-banner" variant={Banner.announcement}>
          <FormattedMessage
            id="network.catalog.banner"
            defaultMessage="Select a <link>Network</link> to view the catalog"
            values={{
              link: (...chunks) => <a href="/networks">{chunks}</a>,
            }}
          />
        </Banner>
      );
    }

    if (!podcastList) {
      if (this.props.status === LOADING) {
        <div data-qa="network-podcast-list-loading" className={styles.container}>
          {this.renderLoader()}
        </div>;
      }

      return null;
    } else {
      return (
        <div data-qa="network-podcast-list" className={styles.container}>
          <Helmet
            title={`Catalog ${
              /* istanbul ignore next */ this.props.networkMetadata
                ? ` - ${this.props.networkMetadata.name}`
                : ''
            }`}
          />
          {this.props.pendingPodcasts && this.props.pendingPodcasts.length > 0 && (
            <PodcastPending
              pendingPodcasts={this.props.pendingPodcasts}
              pendingPodcastsStatus={this.props.pendingPodcastsStatus}
            />
          )}
          {/* istanbul ignore next */ this.props.status === LOADING && this.renderLoader()}
          <ConnectedFilterWithDatePicker
            downloadDataAsCsv={this.handleCsvDownload}
            onUserInput={this.handleSearchInput()}
            csvDownloadTrackingLabel="podcast-list"
            value={this.searchQuery}
          />
          <Table>
            {this.renderColGroup()}
            <thead>{this.renderTableHead()}</thead>
            <tbody>{this.renderTableBody(podcastList.shows)}</tbody>
          </Table>
          <Pagination
            onPageChangeHandler={this.handlePageChange()}
            pageCount={podcastList.totalPages || 0}
          />
        </div>
      );
    }
  }
}

const LinkButton = styled(ButtonPrimary).attrs({
  forwardedAs: 'a',
  buttonSize: ButtonPrimary.sm,
})`
  border-radius: 4px;
  padding: 3px 7px;
  letter-spacing: normal;
  text-transform: none;
  font-weight: bold;
  line-height: 17px;
`;

const WrappedLink = props => {
  return (
    <ThemeProvider theme={getWrappedButtonTheme()}>
      <LinkButton {...props} />
    </ThemeProvider>
  );
};

const selector = createStructuredSelector<
  ReduxState,
  {
    networkPodcastList: ReturnType<typeof selectUserShows>;
    status: ReturnType<typeof selectUserShowsStatus>;
    pendingPodcastsStatus: ReturnType<typeof selectPendingPodcastsStatus>;
    dateRange: ReturnType<typeof selectDateRange>;
    pendingPodcasts: ReturnType<typeof selectPendingPodcasts>;
    networkMetadata: ReturnType<typeof selectNetworkMetadata>;
    currentNetworkId: ReturnType<typeof selectCurrentNetworkId>;
    features: ReturnType<typeof selectFeatures>;
  }
>({
  networkPodcastList: selectUserShows,
  status: selectUserShowsStatus,
  pendingPodcastsStatus: selectPendingPodcastsStatus,
  dateRange: selectDateRange,
  pendingPodcasts: selectPendingPodcasts,
  networkMetadata: selectNetworkMetadata,
  currentNetworkId: selectCurrentNetworkId,
  features: selectFeatures,
});

const actions = { loadUserShows, downloadPodcastListAsCsv, loadPendingPodcasts };
const PodcastList = injectIntl(InternationalizedPodcastList);
export const ConnectedPodcastList = withRouter(connect(selector, actions)(PodcastList));
